import {
  Component,
  JSX,
  Show,
  createEffect,
  createUniqueId,
  onCleanup,
} from "solid-js";
import { Portal } from "solid-js/web";

export type ModalSize = "sm" | "md" | "lg" | "xl";

export type ModalPosition = "center" | "top";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  size?: ModalSize;
  /** Vertical position: "center" (default) or "top" (anchored to top, grows down) */
  position?: ModalPosition;
  class?: string;
  children: JSX.Element;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
};

export const Modal: Component<ModalProps> = (props) => {
  const titleId = createUniqueId();
  const closeOnBackdrop = () => props.closeOnBackdrop ?? true;
  const closeOnEscape = () => props.closeOnEscape ?? true;
  const size = () => props.size ?? "md";
  const position = () => props.position ?? "center";

  let modalRef: HTMLDivElement | undefined;
  let previousActiveElement: Element | null = null;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!props.open) return;

    if (e.key === "Escape" && closeOnEscape()) {
      e.preventDefault();
      props.onClose();
      return;
    }

    if (e.key === "Tab" && modalRef) {
      const focusable = modalRef.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  };

  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget && closeOnBackdrop()) {
      props.onClose();
    }
  };

  createEffect(() => {
    if (props.open) {
      previousActiveElement = document.activeElement;
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";

      requestAnimationFrame(() => {
        const focusable = modalRef?.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        focusable?.focus();
      });

      onCleanup(() => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "";
        if (previousActiveElement instanceof HTMLElement) {
          previousActiveElement.focus();
        }
      });
    }
  });

  return (
    <Show when={props.open}>
      <Portal mount={document.body}>
        <div
          class={`fixed inset-0 z-50 flex justify-center p-4 bg-scrim/40 animate-fade-in ${
            position() === "top" ? "items-start pt-16" : "items-center"
          }`}
          onClick={handleBackdropClick}
          role="presentation"
        >
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={props.title ? titleId : undefined}
            class={
              "w-full bg-surface border border-line rounded-md shadow-overlay animate-scale-in flex flex-col " +
              (position() === "top"
                ? "max-h-[calc(100vh-6rem)] "
                : "max-h-[calc(100vh-4rem)] ") +
              sizeClasses[size()] +
              " " +
              (props.class ?? "")
            }
          >
            <Show when={props.title}>
              <div class="shrink-0 flex items-center justify-between px-4 py-3 border-b border-line">
                <h2 id={titleId} class="text-lg font-semibold text-ink">
                  {props.title}
                </h2>
                <button
                  type="button"
                  class="p-1 text-ink-3 hover:text-ink rounded-sm transition-colors focus-ring"
                  onClick={props.onClose}
                  aria-label="Close modal"
                >
                  <svg
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </Show>
            <div class="p-4 overflow-y-auto flex-1 min-h-0">
              {props.children}
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
};
